import { useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'

import { CALENDAR_EVENT_VIDEO_PLATFORM_DISPLAY_NAME } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/appointmentModalityConstants'
import { VIDEO_PLATFORM } from '@lyrahealth-inc/shared-app-logic/src/models/modality/Modality'
import { Address } from '@lyrahealth-inc/shared-app-logic/src/models/providers/Providers'
import { MEETING_FORMATS } from '@lyrahealth-inc/shared-app-logic/src/models/session/Session'

import { ClientBookingDetails, GetBookingDetailsForClient } from '../ui-models/clients/CalendarClient'

export type ModalityOption = {
  label: string
  value: string
  address?: string
  videoPlatform?: VIDEO_PLATFORM
  helperText?: string
}

export const VIDEO_PLATFORM_OPTION_VALUE: Record<VIDEO_PLATFORM, string> = {
  LYRA: `${MEETING_FORMATS.VIDEO}_LYRA`,
  EXTERNAL: `${MEETING_FORMATS.VIDEO}_EXTERNAL`,
}

type UseBookingDetailsWithModalityOptionsParams = {
  clientId: string | null
  enabled: boolean
  getBookingDetailsForClient?: GetBookingDetailsForClient
  bookableInPersonLocations?: Address[] | null
  splitVideoOptions?: boolean
}

type UseBookingDetailsWithModalityOptionsResult = {
  bookingDetails: ClientBookingDetails | null
  isLoadingClientBookingDetails: boolean
  modalityOptions: ModalityOption[]
}

export const useBookingDetailsWithModalityOptions = ({
  clientId,
  enabled,
  getBookingDetailsForClient,
  bookableInPersonLocations,
  splitVideoOptions = false,
}: UseBookingDetailsWithModalityOptionsParams): UseBookingDetailsWithModalityOptionsResult => {
  const { formatMessage } = useIntl()
  const [bookingDetails, setBookingDetails] = useState<ClientBookingDetails | null>(null)
  const [isLoadingClientBookingDetails, setIsLoadingClientBookingDetails] = useState(false)

  useEffect(() => {
    let isCurrentRequest = true

    if (!enabled || !clientId || !getBookingDetailsForClient) {
      setBookingDetails(null)
      setIsLoadingClientBookingDetails(false)
      return
    }
    setIsLoadingClientBookingDetails(true)
    setBookingDetails(null)
    getBookingDetailsForClient(clientId)
      .then((details) => {
        if (isCurrentRequest) {
          setBookingDetails(details)
        }
      })
      .catch(() => {
        if (isCurrentRequest) {
          setBookingDetails(null)
        }
      })
      .finally(() => {
        if (isCurrentRequest) {
          setIsLoadingClientBookingDetails(false)
        }
      })

    return () => {
      isCurrentRequest = false
    }
  }, [clientId, enabled, getBookingDetailsForClient])

  const modalityOptions = useMemo<ModalityOption[]>(() => {
    return (
      bookingDetails?.meetingFormats.flatMap((option: { value: string; text: string; address?: string }) => {
        if (
          option.value === MEETING_FORMATS.IN_PERSON &&
          bookableInPersonLocations &&
          bookableInPersonLocations.length > 0
        ) {
          const inPersonOptions: ModalityOption[] = []
          let currAddress = 0
          bookableInPersonLocations.forEach((address) => {
            //These fallback options need to exist since street can technically be undefined, but it would
            //mark a more serious issue in the data so these example text are really filler.
            inPersonOptions.push({
              label: address.street1 || 'In-person Location '.concat(currAddress.toString()),
              value: MEETING_FORMATS.IN_PERSON.concat(address.id?.toString() || currAddress.toString()),
              address: [address.street1, address.city, address.state].filter(Boolean).join(', '),
            })
            currAddress++
          })
          return inPersonOptions
        }
        if (splitVideoOptions && option.value === MEETING_FORMATS.VIDEO) {
          return [
            {
              label: formatMessage(
                {
                  defaultMessage: 'Video ({videoPlatformName})',
                  description: 'Label for the Lyra video modality option',
                },
                { videoPlatformName: CALENDAR_EVENT_VIDEO_PLATFORM_DISPLAY_NAME[VIDEO_PLATFORM.LYRA] },
              ),
              value: VIDEO_PLATFORM_OPTION_VALUE.LYRA,
              videoPlatform: VIDEO_PLATFORM.LYRA,
            },
            {
              label: formatMessage(
                {
                  defaultMessage: 'Video ({videoPlatformName})',
                  description: 'Label for the external video modality option',
                },
                { videoPlatformName: CALENDAR_EVENT_VIDEO_PLATFORM_DISPLAY_NAME[VIDEO_PLATFORM.EXTERNAL] },
              ),
              value: VIDEO_PLATFORM_OPTION_VALUE.EXTERNAL,
              videoPlatform: VIDEO_PLATFORM.EXTERNAL,
              helperText: formatMessage({
                defaultMessage: "It's your responsibility to send the video link to clients.",
                description: 'Helper text reminding providers to send the video link when using an external platform',
              }),
            },
          ]
        }
        return { label: option.text, value: option.value, address: option.address }
      }) ?? []
    )
  }, [bookingDetails, formatMessage, splitVideoOptions, bookableInPersonLocations])

  return { bookingDetails, isLoadingClientBookingDetails, modalityOptions }
}
